import { useState } from 'react';
import { partitionThread } from '@l2u/shared/threadPartition';
import { Btn } from './Btn.tsx';
import { CenteredSpinner } from './Spinner.tsx';
import { CommentThread } from './CommentThread.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';
import { ExpiryChip } from './ExpiryChip.tsx';
import { Modal } from './Modal.tsx';
import { formatLocalDay } from '../lib/expiry.ts';
import {
  orderAdoptionHistory,
  orderFileHistory,
  formatFileSize,
  formatUploader,
  type AdoptionHistoryItem,
  type AdoptionVersion,
  type FileHistoryItem,
  type FileVersion,
} from '../lib/fileHistory.ts';
import { buildHistoryTimeline, messageCountLabel } from '../lib/historyTimeline.ts';
import { submissionStatusLabel } from '../lib/submissionStatus.ts';
import type { SubmissionComment } from '../lib/types.ts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function FileVersionRow({ v }: { v: FileVersion }) {
  const uploadedBy = formatUploader(v.uploaded_by_name, v.uploaded_by_role);
  return (
    <li className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-900">{v.file_name}</span>
          {v.state === 'current' ? (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Current
            </span>
          ) : (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
              {v.state === 'removed' ? 'Removed' : 'Replaced'}
            </span>
          )}
          {/* Task 10: a version replaced/removed while still Pending carries no
              review outcome worth showing — the badge already says it.
              Real verdicts (Compliant / Not Compliant / N/A) still show. */}
          {v.state !== 'current' && v.superseded_status && v.superseded_status !== 'pending' && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {submissionStatusLabel(v.superseded_status)}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span>Uploaded {formatDate(v.created_at)}</span>
          <span aria-hidden="true">·</span>
          <span>{formatFileSize(v.size_bytes)}</span>
          {uploadedBy && (
            <>
              <span aria-hidden="true">·</span>
              <span>By {uploadedBy}</span>
            </>
          )}
          {v.expires_at && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1">
                Expiry <ExpiryChip expiresAt={v.expires_at} />
              </span>
            </>
          )}
          {v.state !== 'current' && v.superseded_at && (
            <>
              <span aria-hidden="true">·</span>
              <span>{`${v.state === 'removed' ? 'Removed' : 'Replaced'} ${formatDate(v.superseded_at)}`}</span>
            </>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {v.download_url ? (
          <a
            href={v.download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#4D148C] hover:underline"
          >
            Download
          </a>
        ) : (
          <span className="text-xs italic text-slate-400">Unavailable</span>
        )}
      </div>
    </li>
  );
}

function AdoptionVersionRow({ a }: { a: AdoptionVersion }) {
  return (
    <li className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-900">
            {a.mode === 'adopted' ? 'Standard policy adopted' : a.file_name}
          </span>
          {a.state === 'current' ? (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Current
            </span>
          ) : (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
              {a.archived_reason === 'cleared' ? 'Cleared' : 'Replaced'}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          {a.mode === 'adopted' ? (
            <span>Acknowledged {formatDate(a.acknowledged_at ?? a.recorded_at)}</span>
          ) : (
            <span>Uploaded {formatDate(a.recorded_at)}</span>
          )}
          {a.archived_at && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {a.archived_reason === 'cleared' ? 'Cleared' : 'Replaced'}{' '}
                {formatDate(a.archived_at)}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {a.mode === 'uploaded' &&
          (a.download_url ? (
            <a
              href={a.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#4D148C] hover:underline"
            >
              Download
            </a>
          ) : (
            <span className="text-xs italic text-slate-400">Unavailable</span>
          ))}
      </div>
    </li>
  );
}

// One earlier conversation (between two approvals), collapsed to a single line
// until opened. Read-only: History never offers a composer.
function ConversationRow({
  messages,
  first,
  last,
  initiallyExpanded,
}: {
  messages: SubmissionComment[];
  first: string;
  last: string;
  initiallyExpanded: boolean;
}) {
  const [open, setOpen] = useState(initiallyExpanded);
  return (
    <li className="py-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <span className="text-sm font-medium text-slate-900">
          {`Conversation · ${formatLocalDay(first)}–${formatLocalDay(last)}`}
        </span>
        <span className="shrink-0 text-xs text-slate-500">
          {`${messageCountLabel(messages.length)} ${open ? '▾' : '▸'}`}
        </span>
      </button>
      {open && (
        <div className="mt-3">
          <CommentThread comments={messages} />
        </div>
      )}
    </li>
  );
}

// History for a Documents-tab document: one newest-first timeline of approval
// markers, file/adoption versions and the earlier conversations. The original
// review and the current conversation never appear here.
function HistoryTimeline({
  files,
  adoptions,
  thread,
  initiallyExpanded,
}: {
  files: FileHistoryItem[];
  adoptions: AdoptionHistoryItem[];
  thread: HistoryThread;
  initiallyExpanded: boolean;
}) {
  const partition = partitionThread(thread.comments, thread.approvals);
  const items = buildHistoryTimeline({
    files,
    adoptions,
    approvals: thread.approvals,
    earlier: partition.earlier,
  });

  return (
    <>
      {items.length === 0 ? (
        <p className="text-sm italic text-slate-400">No files have been uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item, i) => {
            switch (item.kind) {
              case 'approval':
                return (
                  <li
                    key={`approval-${i}`}
                    className="py-2 text-center text-xs font-medium text-slate-500"
                  >
                    {`Approved · ${formatLocalDay(item.at)}`}
                  </li>
                );
              case 'conversation':
                return (
                  <ConversationRow
                    key={`conversation-${item.index}`}
                    messages={item.messages}
                    first={item.first}
                    last={item.last}
                    initiallyExpanded={initiallyExpanded}
                  />
                );
              case 'file':
                return <FileVersionRow key={`file-${item.version.id}`} v={item.version} />;
              case 'adoption':
                return <AdoptionVersionRow key={`adoption-${item.version.id}`} a={item.version} />;
            }
          })}
        </ul>
      )}
      {partition.original.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">{thread.originalNote}</p>
      )}
    </>
  );
}

type HistoryThread = {
  /** The whole thread this portal can see (vendor: visible only; admin: incl. drafts). */
  comments: SubmissionComment[];
  /** Ascending approval moments; the modal partitions with partitionThread. */
  approvals: string[];
  /** Footer shown only when the original review has messages (portal-specific wording). */
  originalNote: string;
};

// Every version of the document(s) uploaded against one submission: the live
// file(s) labelled "Current", replaced ones "Replaced", removed ones "Removed"
// (rows removed before 0096 carry no marker and still read "Replaced"), newest-first,
// plus the policy-document choice history. Presentational only — the admin and
// vendor apps each fetch from their own file-history endpoint (same response
// shape) and pass the result in, so both portals show the same list.
//
// With `thread` (a Documents-tab document) the same rows become one timeline
// with approval markers and the earlier conversations; without it the layout
// is unchanged.
export function FileHistoryModal({
  questionLabel,
  loading,
  error,
  files,
  adoptions,
  onClose,
  thread,
  initiallyExpanded = false,
}: {
  questionLabel: string;
  loading: boolean;
  error: unknown;
  files: FileHistoryItem[];
  adoptions: AdoptionHistoryItem[];
  onClose: () => void;
  thread?: HistoryThread;
  /** Test seam: every conversation row starts open. Default false (collapsed). */
  initiallyExpanded?: boolean;
}) {
  const versions = orderFileHistory(files);
  const adoptionVersions = orderAdoptionHistory(adoptions);

  return (
    <Modal
      open
      size="lg"
      title={`History — ${questionLabel}`}
      onClose={onClose}
      footer={
        <Btn variant="secondary" size="sm" onClick={onClose}>
          Close
        </Btn>
      }
    >
      {loading ? (
        <CenteredSpinner />
      ) : error != null ? (
        <ErrorMessage error={error} />
      ) : thread ? (
        <HistoryTimeline
          files={files}
          adoptions={adoptions}
          thread={thread}
          initiallyExpanded={initiallyExpanded}
        />
      ) : versions.length === 0 && adoptionVersions.length === 0 ? (
        <p className="text-sm italic text-slate-400">No files have been uploaded yet.</p>
      ) : (
        <>
          {versions.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {versions.map((v) => (
                <FileVersionRow key={v.id} v={v} />
              ))}
            </ul>
          )}
          {adoptionVersions.length > 0 && (
            <div className={versions.length > 0 ? 'mt-4' : ''}>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Policy document choice
              </h4>
              <ul className="divide-y divide-slate-100">
                {adoptionVersions.map((a) => (
                  <AdoptionVersionRow key={a.id} a={a} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
